import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  styled,
} from '@mui/material';

/**
 * Main container for AccordionCard
 * Uses theme design system values for all styling
 */
export const StyledAccordionCard = styled(Box)<{ isExpanded?: boolean }>(
  ({ theme, isExpanded }) => ({
    border: `1px solid ${isExpanded ? theme.palette.info.main : theme.palette.text.fifth}`,
    borderRadius: theme.customShape.borderRadius2XL,
    borderBottomLeftRadius: isExpanded
      ? theme.customShape.borderRadiusLarge
      : theme.customShape.borderRadiusXLarge,
    borderBottomRightRadius: isExpanded
      ? theme.customShape.borderRadiusLarge
      : theme.customShape.borderRadiusXLarge,
    overflow: 'hidden',
  }),
);

/**
 * MUI Accordion wrapper
 * Removes default MUI Accordion styling
 */
export const StyledAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

/**
 * Header container
 * Uses background-contrast color (#f9f9ff) from Figma for all sizes
 * Expand icon positioned on the left side using CSS order
 */
export const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  minHeight: 'auto',
  display: 'flex',
  '&.Mui-expanded': {
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing(2.5),
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiAccordionSummary-content': {
      flexDirection: 'column',
    },
  },
}));

/**
 * Left side of header
 * Contains expand icon and header content
 */
export const StyledHeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  flex: 1,

  [theme.breakpoints.down('md')]: {
    width: '100%',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
}));

/**
 * Expand/collapse icon wrapper
 * Points right when closed, rotates -90deg to point down when expanded
 */
export const StyledExpandIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['transform']),
  transform: 'rotate(0deg)', // Default: pointing right
  '&.Mui-expanded': {
    transform: 'rotate(90deg) ', // Rotate -90deg (counter-clockwise) to point down when expanded
  },
  [theme.breakpoints.down('md')]: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * Right side of header
 * Contains action buttons
 */
export const StyledHeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginRight: theme.spacing(-8),
    marginLeft: theme.spacing(-8),
  },
}));

/**
 * Content area
 * Expandable section with indented padding
 * Uses background-contrast color (#f9f9ff) from Figma for all sizes
 */
export const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 10, 6, 10),
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
}));

/**
 * Divider between header and content
 * Uses #BBBFBF color from Figma for all sizes
 */
export const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.text.fourth, // Divider color from Figma
}));

/**
 * Footer container
 * Uses blue-background color (#eef0ff) from Figma for all sizes
 */
export const StyledFooter = styled(Box)<{ isExpanded?: boolean }>(
  ({ theme, isExpanded }) => ({
    backgroundColor: isExpanded
      ? theme.palette.background.contrast
      : theme.palette.background.default,
    padding: theme.spacing(2, 10),
    borderBottomLeftRadius: theme.customShape.borderRadiusLarge,
    borderBottomRightRadius: theme.customShape.borderRadiusLarge,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      flexDirection: 'column',
    },
  }),
);

/**
 * Left side of footer
 * Contains summary information
 */
export const StyledFooterLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(8),
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
    width: '100%',
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
  },
}));

/**
 * Right side of footer
 * Contains total amount
 */
export const StyledFooterRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'center',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'flex-end',
    width: '100%',
    paddingTop: theme.spacing(2),
  },
}));
